import type { UUID } from 'node:crypto';

import { Box, Typography } from '@mui/material';
import React from 'react';
import { useDropzone } from 'react-dropzone';

import { NewEmailAttachmentModel } from '@/components/emailWindow/components/attachments/model/NewEmailAttachment.model.ts';
import { useAddAttachment } from '@/components/emailWindow/components/attachments/util/useAddAttachment.util.ts';
import { useAnyAttachmentIsNotReady } from '@/components/emailWindow/util/useAnyAttachmentIsNotReady.util.ts';
import { themeColors } from '@/config/theme/themeColors.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import {
  NEW_EMAIL_REDUCER_EVENT,
  useNewEmailUpdateState,
} from '@/views/emails/context/NewEmails.ts';

type Props = {
  emailId: string;
};

export const NewEmailAttachmentsUploadField: React.FC<Props> = ({ emailId }) => {
  const { t } = useTranslationWithPrefix('views.emails.newEmail.body.dropzone');
  const { addAttachment, isAddingAttachment } = useAddAttachment();
  const updateNewEmailState = useNewEmailUpdateState();
  const anyAttachmentIsNotReady = useAnyAttachmentIsNotReady();

  const handleAddNewAttachment = (uuid: UUID, file: File) => {
    const attachment: NewEmailAttachmentModel = {
      contentType: file.type,
      isReady: false,
      name: file.name,
      uuid,
    };
    updateNewEmailState({
      payload: attachment,
      type: NEW_EMAIL_REDUCER_EVENT.ATTACHMENT_ADD,
    });
  };

  const handleDrop = (acceptedFiles: File[]) => {
    void (async () => {
      const attachmentUUID = crypto.randomUUID();
      handleAddNewAttachment(attachmentUUID, acceptedFiles[0]);
      const { attachmentId } = await addAttachment({
        emailId,
        file: acceptedFiles[0],
      });
      updateNewEmailState({
        payload: { attachmentId, uuid: attachmentUUID },
        type: NEW_EMAIL_REDUCER_EVENT.ATTACHMENT_UPDATE_ID,
      });
    })();
  };

  const { getInputProps, getRootProps } = useDropzone({
    multiple: false,
    onDrop: handleDrop,
  });

  const isDisabled = isAddingAttachment || anyAttachmentIsNotReady;

  return (
    <Box
      {...getRootProps({
        className: 'dropzone',
        style: { cursor: isDisabled ? 'wait' : 'pointer' },
      })}
      sx={{
        justifyContent: 'center',
        minHeight: '40px',
      }}
    >
      <input {...getInputProps({ disabled: isDisabled })} />
      <Typography sx={{ color: themeColors.greyDark, textAlign: 'center' }}>
        {isDisabled ? t('disabled') : t('label')}
      </Typography>
    </Box>
  );
};
